import React from "react";
import { withStyles, Theme, createStyles, WithStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import dayjs from './RosterDayJS';
import { XGrid, GridCellParams, GridColDef, GridDensityTypes  } from '@material-ui/x-grid';
import { Roster, Shift } from "./models/api";
import DepartmentChip from "./DepartmentChip";
import { WithData } from "./WithData";
import memoize from "memoize-one";
import { rosterGridRows, RosterGridRow } from './models/datagrid';
import ShiftsDataGridCell from "./ShiftsDataGridCell";

export interface RosterTableProps {
  startDate: Date
}

interface RosterTableState {
  disabledDepartments: Set<string>
}

const shiftsCellRenderer = (params: GridCellParams) => {
  const shifts = params.value as Shift[];

  if (!shifts) {
    return null;
  }
  
  return <ShiftsDataGridCell shifts={shifts} />
}

const dayColumnTemplate: Partial<GridColDef> = { 
  flex: 1, 
  align: 'center', 
  headerAlign: 'center', 
  cellClassName: (params: GridCellParams) => { 
    if (params.value) {
      if ((params.value as Shift[]).some(shift => shift.person === null)) {
        return "has-unrostered-shift";
      }
    }
    return "";
  }, 
  disableColumnMenu: true, 
  disableReorder: true, 
  hideSortIcons: true, 
  renderCell: 
  shiftsCellRenderer 
};

const dayColumn = (startDate: Date, dayIndex: number): GridColDef => {
  let date = dayjs(startDate);
  if (dayIndex > 0) {
    date = date.add(dayIndex, 'd');
  }
  
  return {...dayColumnTemplate, field: 'day' + (dayIndex + 1), headerName: date.format('ddd MMM Do') };
}

const StyledDataGrid = withStyles(({ typography}: Theme) => createStyles({
  root: {
    '& .MuiDataGrid-cell': {
      lineHeight: 'unset !important',
      whiteSpace: 'normal',
      'flex-wrap': 'wrap',
      display: 'flex',
      'align-content': 'center',
      'justify-content': 'center',
      'border-radius': 2.5,
    },
    '& .MuiDataGrid-cell.has-unrostered-shift': {
      background: 'rgb(253, 236, 234)',
    },
    '& .MuiDataGrid-cell--textRight': {
      'justify-content': 'flex-end',
      'font-weight': typography.fontWeightMedium
    },
  }
}))(XGrid);

export class RosterDataGrid extends React.PureComponent<RosterTableProps & WithData, RosterTableState> {

  constructor(props: RosterTableProps & WithData) {
    super(props);
    this.state = { disabledDepartments: new Set<string>() };
  }
        
  private readonly columns: GridColDef[] = [
    { field: 'role', flex: 1, align: 'right', headerAlign: 'right', hideSortIcons: true, headerName: 'Role' },
    { field: 'department', flex: 1, align: 'center', headerAlign: 'center', hideSortIcons: true, headerName: 'Department' },
    dayColumn(this.props.startDate, 0),
    dayColumn(this.props.startDate, 1),
    dayColumn(this.props.startDate, 2),
    dayColumn(this.props.startDate, 3),
    dayColumn(this.props.startDate, 4),
    dayColumn(this.props.startDate, 5),
    dayColumn(this.props.startDate, 6)
  ];

  private readonly rows = memoize((roster: Roster, disabledDepartments: Set<string>): RosterGridRow[] => {
    let rows: RosterGridRow[] = rosterGridRows(roster);

    if (disabledDepartments.size > 0) {
      rows = rows.filter(row => !disabledDepartments.has(row.department));
    }

    return rows;
  });

  private readonly departments = memoize((roster: Roster): string[] => Object.keys(roster || { }));
  
  private departmentToggledHandler = (args: OnDepartmentToggledArgs) => {
    this.setState(previous => {
      const { disabledDepartments } = previous;

      if (args.enabled) {
        disabledDepartments.delete(args.department);
      } else {
        disabledDepartments.add(args.department);
      }

      return { disabledDepartments: new Set(disabledDepartments) }
    });
  }

  render() {
    const { loading, data } = this.props;
    const rows = this.rows(data, this.state.disabledDepartments);
    const departments = this.departments(data);

    return (
          <StyledDataGrid autoHeight loading={loading} density={GridDensityTypes.Compact} rows={rows} columns={this.columns} components={{
        Footer: StyledRosterTableFooter,
      }} componentsProps={{ footer: { departments, onDepartmentToggle: this.departmentToggledHandler} } } />
      );
  }
}

export type OnDepartmentToggledHandler = (args: OnDepartmentToggledArgs) => void;

export type OnDepartmentToggledArgs = {
  department: string,
  enabled: boolean
}

const footerStyles = ({ spacing }: Theme) => createStyles({
  root: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: spacing(0.5),
    margin: 0,
  }
});

class RosterTableFooter extends React.PureComponent<{ departments: string[], onDepartmentToggle?: OnDepartmentToggledHandler } & WithStyles<typeof footerStyles>> {
  render() {
    const { classes } = this.props;
    return <Paper component="ul" className={classes.root}>
        { this.props.departments.map((department: string) => <DepartmentChip key={department} department={department} onToggle={state => this.props.onDepartmentToggle?.({ department: department, enabled: state })} />)}
    </Paper>
  }
}

const StyledRosterTableFooter = withStyles(footerStyles)(RosterTableFooter);